import { html, nothing, PropertyValues, render, RootPart } from 'lit';
import { Constructor } from 'lit-element';
import { property, state } from 'lit/decorators.js';
import { GridItemModel } from '@vaadin/vaadin-grid';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import { DataTableEvents, DefaultColumnKeys, IDataTableColumn } from '../types';
import { ButtonColor, ButtonSize } from '../../gzIconButton/types';

// all public attributes and methods from the decorator class should be exposed in the interface
export interface RowReorderingInterface {
  allowRowReordering: boolean;
  resetPriority(): void;
}

// all protected attributes and methods from the decorator class, used by other decorators, should be exposed here
export interface RowReorderingProtectedInterface {
  rowReorderingDisabled: boolean;
}

export const rowReordering = <T extends Constructor<GzDataTableWebComponent<TDataTableItem>>, TDataTableItem = unknown>(
  dataTableClass: T
) => {
  class RowReorderingDecorator extends dataTableClass {
    /**
     * If set, it allows reordering rows by dragging and dropping them. It adds on each row a reordering handle on
     * the <code>DefaultColumnKeys.REORDER</code> column, the priority info on the
     * <code>DefaultColumnKeys.PRIORITY</code> column, and a reset priority button in the footer, on the right
     */
    @property({ type: Boolean, attribute: true, reflect: true })
    allowRowReordering = false;

    @property({ type: Boolean, attribute: true, reflect: true })
    private resetPriorityButtonRendered = false; // only used for applying CSS via attribute matching

    @state()
    protected rowReorderingDisabled = false;

    @state()
    private dragEventListenersAttached = false;

    @state()
    private initialOrderMap: Map<TDataTableItem, number> = new Map();

    private readonly reorderColumnWidth = 40; // px
    private readonly priorityColumnWidth = 64; // px

    private draggedRow: TDataTableItem | null = null;
    private initialPosition: number | undefined;

    connectedCallback(): void {
      super.connectedCallback();
      this.updateComplete.then(() => {
        if (this.grid && this.allowRowReordering && !this.rowReorderingDisabled) {
          this.enableReordering();
          this.updateEventListeners();
        }
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListeners();
    }

    updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);
      if (changedProperties.has('items')) {
        this.assignOrder();
      }
      if (changedProperties.has('currentItems') && changedProperties.get('currentItems') !== undefined) {
        this.resetPriorityButtonRendered = false;
        if (this.currentItems.length > changedProperties.get('currentItems').length) {
          this.assignOrderToNewRow();
        }
      }

      if (changedProperties.has('rowReorderingDisabled') || changedProperties.has('allowRowReordering')) {
        this.onRowReorderingChange();
        this.updateEventListeners();
      }
    }

    resetPriority() {
      this.currentItems.sort((a, b) => (this.initialOrderMap.get(a) ?? 0) - (this.initialOrderMap.get(b) ?? 0));
      this.currentItems = [...this.currentItems];
      this.dispatchReorderingEvent(DataTableEvents.ROWS_RESET_ORDER, this.currentItems);
    }

    protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
      // overridden function
      const columnElement = super.renderColumn(column, index);
      if (!this.allowRowReordering) {
        return columnElement;
      }
      if (column.key === DefaultColumnKeys.REORDER) {
        return this.configReorderColumn(columnElement, index);
      }
      if (column.key === DefaultColumnKeys.PRIORITY) {
        return this.configPriorityColumn(columnElement, index);
      }
      if (this.shouldRenderResetPriorityFooter(index)) {
        this.resetPriorityButtonRendered = true;
        columnElement.footerRenderer = root =>
          this.instance.renderFooter(
            root,
            this.strings.resetPriorityButton,
            this.resetPriority.bind(this),
            'reset-priority-footer',
            this.rowReorderingDisabled
          );
      }
      return columnElement;
    }

    private configReorderColumn(columnElement: GridColumnElement, index: number): GridColumnElement {
      Object.assign(columnElement, {
        frozen: true,
        resizable: false,
        flexGrow: 0,
        width: this.reorderColumnWidth + 'px',
        autoWidth: false,
      });
      columnElement.renderer = root => this.renderReorderColumnCell(root);
      if (this.instance.shouldRenderEmptyStateFooter(index)) {
        columnElement.footerRenderer = root =>
          this.instance.renderFooter(
            root,
            this.instance.strings.emptyStateFooterButton,
            this.instance.onEmptyStateFooterButtonClick,
            'reordering-footer'
          );
      }
      return columnElement;
    }

    private configPriorityColumn(columnElement: GridColumnElement, index: number): GridColumnElement {
      Object.assign(columnElement, {
        resizable: false,
        flexGrow: 0,
        width: this.priorityColumnWidth + 'px',
        autoWidth: false,
      });
      columnElement.renderer = (root, _, rowData) => this.renderPriorityColumnCell(root, rowData);
      return columnElement;
    }

    private renderReorderColumnCell(root: HTMLElement): RootPart | undefined {
      return render(
        html`
          <gz-icon
            name="ds-icon-drag"
            size="${ButtonSize.SIZE_24}"
            class="default-icon ${this.rowReorderingDisabled ? 'disabled' : nothing}"
            color="${ButtonColor.SECONDARY}"
          >
          </gz-icon>
        `,
        root
      );
    }

    private handleDragStart = (event: DragEvent): void => {
      // @ts-ignore
      if ('draggedItems' in event.detail) {
        // @ts-ignore
        this.draggedRow = event.detail.draggedItems[0];
        // @ts-ignore
        this.initialPosition = this.currentItems.indexOf(this.draggedRow);
      }
    };

    private handleDrop = (event: DragEvent): void => {
      this.instance.deselectAll();
      event.preventDefault();

      // @ts-ignore
      const { dropTargetItem, dropLocation } = event.detail;

      if (this.draggedRow && dropTargetItem !== this.draggedRow && this.initialPosition !== undefined) {
        // Remove row from current position
        this.currentItems.splice(this.initialPosition, 1);
        // Re-insert the row at the new position
        const dropIndex = this.currentItems.indexOf(dropTargetItem) + (dropLocation === 'below' ? 1 : 0);
        this.currentItems.splice(dropIndex, 0, this.draggedRow);

        // Re-assign the array to refresh the grid
        this.currentItems = [...this.currentItems];
        this.draggedRow = null;
        this.initialPosition = undefined;

        if (this.grid && this.grid.hasAttribute('dragging-rows')) {
          this.grid.removeAttribute('dragging-rows');
        }

        this.dispatchReorderingEvent(DataTableEvents.ROWS_REORDERED, this.currentItems);
      }
    };

    private dispatchReorderingEvent(event: DataTableEvents, items: TDataTableItem[]): void {
      this.dispatchEvent(
        new CustomEvent(event, {
          detail: { items: items },
          bubbles: true,
          composed: true,
        })
      );
    }

    private renderPriorityColumnCell(root: HTMLElement, rowData: GridItemModel | undefined): RootPart | undefined {
      if (!rowData || !rowData.item) {
        return;
      }
      return render(html` <span class="cell-label priority-cell">${rowData.index + 1}</span> `, root);
    }

    private shouldRenderResetPriorityFooter(columnIndex: number): boolean {
      return !isEmpty(this.currentItems) && this.currentItems.length > 1 && columnIndex === this.columns.length - 1;
    }

    private assignOrder(): void {
      if (!this.allowRowReordering) {
        return;
      }
      if (isEmpty(this.currentItems)) {
        return;
      }
      this.currentItems.forEach((item, index) => {
        this.initialOrderMap.set(item, index);
      });
    }

    private assignOrderToNewRow(): void {
      const lastItem = this.currentItems[this.currentItems.length - 1];
      const maxOrder = Array.from(this.initialOrderMap.values()).reduce((max, value) => Math.max(max, value), -1);
      this.initialOrderMap.set(lastItem, maxOrder + 1);
    }

    private onRowReorderingChange(): void {
      this.rowReorderingDisabled || !this.allowRowReordering ? this.disableReordering() : this.enableReordering();
    }

    private enableReordering(): void {
      if (!this.grid) {
        return;
      }
      this.grid.rowsDraggable = true;
      this.grid.dropMode = 'between';
      this.grid.setAttribute('part', 'row-reordering-allowed');
    }

    private updateEventListeners(): void {
      this.rowReorderingDisabled ? this.removeEventListeners() : this.addEventListeners();
    }

    private addEventListeners(): void {
      if (!this.instance.disabled && !this.dragEventListenersAttached && this.grid) {
        this.grid.addEventListener('grid-dragstart', event => this.handleDragStart(event as DragEvent));
        this.grid.addEventListener('grid-drop', event => this.handleDrop(event as DragEvent));
        this.dragEventListenersAttached = true;
      }
    }

    private removeEventListeners(): void {
      if (this.dragEventListenersAttached) {
        this.removeEventListener('grid-dragstart', event => this.handleDragStart(event as DragEvent));
        this.removeEventListener('grid-drop', event => this.handleDrop(event as DragEvent));
        this.dragEventListenersAttached = false;
      }
    }

    private disableReordering(): void {
      if (!this.grid) {
        return;
      }
      this.grid.rowsDraggable = false;
      this.grid.dropMode = undefined;
      this.grid.removeAttribute('part');
    }
  }

  return RowReorderingDecorator;
};
